import { fromPairs } from 'lodash';
import { Point3Type } from '../gql/graphql';

function loadManifest(documentId: string): Promise<Autodesk.Viewing.Document> {
  return new Promise((resolve, reject) => {
    Autodesk.Viewing.Document.load(
      documentId,
      (doc) => {
        resolve(doc);
      },
      (error) => {
        reject(error);
      }
    );
  });
}

export type ViewerLoadModel = {
  id: string;
  urn: string;
  activeViewGuid?: string | null;
};

export const loadModels = async (
  view: Autodesk.Viewing.AggregatedView,
  models: ViewerLoadModel[],
  areOverlaysTemporary = false
): Promise<Record<string, Autodesk.Viewing.Model>> => {
  const documents = await Promise.all(
    models.map((m) => loadManifest(`urn:${m.urn}`))
  );

  const bubbleNodes = documents.map((doc, index) => {
    const bubbles = doc.getRoot().search({
      type: 'geometry',
      role: '3d',
    }) as Autodesk.Viewing.BubbleNode[];

    if (bubbles.length <= 1) {
      return bubbles[0] ?? null;
    }

    const activeGuid = models[index]?.activeViewGuid;
    if (activeGuid) {
      const matchingBubble = bubbles.find(
        (bubble) =>
          Array.isArray(bubble.children) &&
          bubble.children.some((child) => child?.data?.guid === activeGuid)
      );
      if (matchingBubble) {
        return matchingBubble;
      }
    }

    return bubbles[0] ?? null;
  });

  let loadedModels: Array<Autodesk.Viewing.Model>;
  if (areOverlaysTemporary) {
    loadedModels = await Promise.all(
      bubbleNodes.map((node) => view.show(node, { keepCurrentModels: true }))
    );
  } else {
    loadedModels = await view.switchView(bubbleNodes, undefined);
  }

  return fromPairs(loadedModels.map((model) => [model.getData().urn, model]));
};

export async function initializeViewer(
  intoElement: HTMLDivElement,
  globalOffset: Point3Type
): Promise<Autodesk.Viewing.AggregatedView> {
  console.log('setting up viewer');

  const aggregateView = new Autodesk.Viewing.AggregatedView();
  await aggregateView.init(intoElement, {
    getCustomLoadOptions: () => {
      return {
        applyRefPoint: false,
        globalOffset: new THREE.Vector3(
          globalOffset.x,
          globalOffset.y,
          globalOffset.z
        ),
      };
    },
  });

  await aggregateView.viewer.loadExtension('Autodesk.Snapping');
  /* eslint-disable @typescript-eslint/ban-ts-comment */
  // @ts-ignore
  if (window.Cypress) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.SPARKEL_3D_VIEWER = aggregateView.viewer;
  }
  return aggregateView;
}
